import { useState, useEffect } from "react";

const COLORS = {
  navy: "#003366",
  navyDark: "#001f44",
  navyMid: "#004080",
  red: "#cc0000",
  greyPage: "#f0f2f5",
  greyCard: "#e5e8ed",
  greyMid: "#c8cdd6",
  greyDark: "#555e6d",
  white: "#ffffff",
  textDark: "#1a2233",
  textMid: "#3d4a5c",
};

const IMAGES = {
  logoWhite: "https://arcuchicnc.com/wp-content/uploads/2023/07/Untitled-6.png",
  logoFooter: "https://arcuchicnc.com/wp-content/uploads/2023/07/Untitled-6.png",
  // IMM Controller page images
  immController: "https://arcuchicnc.com/wp-content/uploads/2023/09/1-1.png",
  wireController: "https://arcuchicnc.com/wp-content/uploads/2025/08/wire.png",
  stampController: "https://arcuchicnc.com/wp-content/uploads/2025/08/stamp.png",
  hydraulicController: "https://arcuchicnc.com/wp-content/uploads/2025/08/hyraluic.png",
  // Servo system page images
  servoInjection: "https://arcuchicnc.com/wp-content/uploads/2025/08/injection.png",
  servoElectro: "https://arcuchicnc.com/wp-content/uploads/2025/08/electro.png",
  servoWire: "https://arcuchicnc.com/wp-content/uploads/2025/08/wire-1.png",
  servoUnit: "https://arcuchicnc.com/wp-content/uploads/2023/09/1-2-768x616.png",
  servo1: "https://arcuchicnc.com/wp-content/uploads/2025/08/1.jpg",
  servo2: "https://arcuchicnc.com/wp-content/uploads/2025/08/2.jpg",
  servo3: "https://arcuchicnc.com/wp-content/uploads/2025/08/3.jpg",
  servo4: "https://arcuchicnc.com/wp-content/uploads/2025/08/4.jpg",
  servo5: "https://arcuchicnc.com/wp-content/uploads/2025/08/5.jpg",
  servo6: "https://arcuchicnc.com/wp-content/uploads/2025/08/6.jpg",
  servo7: "https://arcuchicnc.com/wp-content/uploads/2025/08/7.jpg",
  // Frequency converter page images
  freq1: "https://arcuchicnc.com/wp-content/uploads/2025/08/1.png",
  freq2: "https://arcuchicnc.com/wp-content/uploads/2025/08/2.png",
  // Machine / scene images
  factory: "https://arcuchicnc.com/wp-content/uploads/2023/07/sdfds.jpg",
  inject: "https://arcuchicnc.com/wp-content/uploads/2025/08/INJECT.jpg",
  wire: "https://arcuchicnc.com/wp-content/uploads/2025/08/WIRE.jpg",
  stamping: "https://arcuchicnc.com/wp-content/uploads/2025/08/Stamping-Forming-Solution-1.jpg",
  hydraulic: "https://arcuchicnc.com/wp-content/uploads/2025/08/HYDRAULIC.jpg",
  blogHydraulic: "https://arcuchicnc.com/wp-content/uploads/2024/04/Let-us-show-yu-the-way.-1-1024x538.png",
};

const PRODUCTS = [
  {
    id: "imm-controller",
    tag: "Controller",
    tagColor: COLORS.red,
    icon: "🖥️",
    title: "IMM Controller",
    fullName: "Injection Molding Machine Controller",
    subtitle: "Advanced PLC control system for injection molding, wire forming, stamping & hydraulic press machines",
    img: IMAGES.immController,
    imgBg: COLORS.greyPage,
    heroImg: IMAGES.inject,
    extraImgs: [IMAGES.wireController, IMAGES.stampController, IMAGES.hydraulicController],
    desc: "An advanced PLC control system designed to optimize the operation of injection molding machines and other industrial forming equipment. Used in manufacturing processes where precision control of temperature, pressure, injection speed, and cooling determines product quality and production efficiency.",
    highlights: [
      "Easy-to-use, user-friendly interface enabling operators to set up and control the injection molding process efficiently",
      "High-precision management of temperature, pressure, injection speed, and cooling for consistent product quality",
      "Customizable settings tailored for different molds and materials",
      "Real-time monitoring with continuous tracking of critical parameters and prompt anomaly detection",
      "Automatic fault diagnosis with alarm systems for rapid troubleshooting and minimal downtime",
      "Energy-efficient design to reduce operating costs and environmental impact",
      "Seamless integration with factory management systems and full data logging capability",
    ],
    specs: [
      { label: "Compatible Machines", value: "IMM, Wire Forming, Stamping, Hydraulic Press" },
      { label: "Interface", value: "User-friendly touchscreen HMI" },
      { label: "Temperature Control", value: "Multi-zone precision PID management" },
      { label: "Monitoring", value: "Real-time parameter tracking & anomaly alerts" },
      { label: "Fault Diagnosis", value: "Automatic fault detection with operator alarms" },
      { label: "Connectivity", value: "Factory management system integration" },
      { label: "Data Logging", value: "Full production data recording capability" },
      { label: "Energy Saving", value: "Optimized power management built-in" },
    ],
    applications: ["Plastic injection molding machines", "Wire forming & spring coiling machines", "Stamping forming machines", "Hydraulic press forming machines"],
    compatible: ["All major IMM brands", "Wire benders & spring coilers", "Stamping & transfer presses", "Hydraulic forming presses"],
  },
  {
    id: "servo-system",
    tag: "Servo System",
    tagColor: "#0055aa",
    icon: "⚙️",
    title: "Servo System",
    fullName: "Industrial AC Servo Drive & Motor System",
    subtitle: "Precise motion control in CNC machines and industrial automation — regulating voltage and current to servo motors for accurate, repeatable movement",
    img: IMAGES.servoInjection,
    imgBg: COLORS.white,
    heroImg: IMAGES.hydraulic,
    extraImgs: [IMAGES.servoElectro, IMAGES.servoWire, IMAGES.servoUnit],
    desc: "Arcuchi servo systems ensure precise motion control in CNC machines and industrial automation, regulating voltage and current to servo motors and guaranteeing accurate and repeatable movement for high-performance applications. Available in three specialized variants: Electro-Hydraulic Servo, Electric Pre-Plasticizing Servo, and Wire Forming Machine Servo.",
    highlights: [
      "Electro-Hydraulic Servo: wide voltage support 380–480 V across multiple power levels",
      "Built-in compliance: integrated filter and reactor, CE certified for injection molding, die casting & wind power",
      "Electric Pre-Plasticizing Servo: over 98% transmission efficiency with helical gear design for quiet operation",
      "Up to 140,000 Nm torque capacity with low temperature rise and minimal maintenance requirements",
      "Wire Forming Servo: voltage range 220 V and 380–480 V (0.1–160 kW)",
      "Encoder compatibility: resolvers, Endat, BISS-C — flexible bus: pulse, analog, EtherCAT, CANopen, PROFINET",
      "CE and RoHS certified with STO safety function; encoder cables support up to 50 metres",
    ],
    specs: [
      { label: "Electro-Hydraulic Voltage", value: "380 V – 480 V (multi power levels)" },
      { label: "Wire Forming Voltage", value: "220 V / 380 V – 480 V" },
      { label: "Wire Forming Power Range", value: "0.1 kW – 160 kW" },
      { label: "Transmission Efficiency", value: "> 98% (Pre-Plasticizing model)" },
      { label: "Max Torque", value: "Up to 140,000 Nm" },
      { label: "Encoder Support", value: "Resolver, Endat, BISS-C" },
      { label: "Bus Options", value: "Pulse, Analog, EtherCAT, CANopen, PROFINET" },
      { label: "Certifications", value: "CE, RoHS, STO safety function" },
    ],
    applications: ["Injection molding servo hydraulic drive", "Die casting machines", "Wind power equipment", "Wire bending & spring coiling", "Electric pre-plasticizing axes"],
    compatible: ["Injection molding machines", "Die casting machines", "Wire forming machines", "Hydraulic presses", "CNC industrial automation"],
  },
  {
    id: "frequency-converter",
    tag: "Drive System",
    tagColor: "#006644",
    icon: "🔌",
    title: "Frequency Converter",
    fullName: "Variable Frequency Drive (Arcuchi FC Series)",
    subtitle: "CE/UL certified, RoHS compliant VFDs supporting VVVF/SVC for induction and PMSM motors — 4 to 500 kW",
    img: IMAGES.freq1,
    imgBg: COLORS.white,
    heroImg: IMAGES.blogHydraulic,
    extraImgs: [IMAGES.freq2],
    desc: "Arcuchi frequency converters are globally certified (CE/UL, RoHS compliant) variable frequency drives supporting VVVF and SVC control modes for both induction and PMSM motors. With a power range of 4–500 kW, 0.01 Hz frequency resolution up to 590 Hz, smart auto-tuning, and USB parameter cloning, they deliver precise speed control across industrial applications.",
    highlights: [
      "Globally certified: CE/UL standards compliant, RoHS certified",
      "Supports VVVF and SVC control modes for induction and PMSM motors",
      "Power coverage: 4–500 kW with 0.01 Hz frequency resolution (up to 590 Hz)",
      "Smart auto-tuning of carrier frequency and voltage for optimal motor performance",
      "Multi-protection capabilities: over-current, over-voltage, under-voltage, overheat, stall",
      "USB parameter cloning for fast commissioning and parameter backup across drives",
      "Seamless integration with PLCs and SCADA via RS-485 Modbus",
    ],
    specs: [
      { label: "Power Range", value: "4 kW – 500 kW" },
      { label: "Frequency Resolution", value: "0.01 Hz (up to 590 Hz output)" },
      { label: "Control Mode", value: "VVVF / SVC (Sensorless Vector)" },
      { label: "Motor Support", value: "Induction motors & PMSM" },
      { label: "Certifications", value: "CE, UL, RoHS compliant" },
      { label: "Auto-tuning", value: "Carrier frequency & voltage auto-adjustment" },
      { label: "Parameter Backup", value: "USB parameter cloning" },
      { label: "Communication", value: "RS-485 Modbus RTU" },
    ],
    applications: ["Variable-speed pump & fan drives", "Conveyor & material handling", "Injection molding auxiliary drives", "HVAC and cooling systems"],
    compatible: ["Standard 3-phase induction motors", "Permanent magnet synchronous motors (PMSM)", "Pump & fan (quadratic) loads", "Constant torque applications"],
  },
  {
    id: "retrofit",
    tag: "Upgrade Service",
    tagColor: "#5500aa",
    icon: "🔧",
    title: "Machine Retrofit",
    fullName: "Machine Retrofit & Upgrade Solutions",
    subtitle: "Modernize legacy injection molding, hydraulic press, wire forming and stamping machines with new Arcuchi control architecture",
    img: IMAGES.stampController,
    imgBg: COLORS.white,
    heroImg: IMAGES.stamping,
    extraImgs: [],
    desc: "A machine retrofit from Arcuchi breathes new life into aging equipment without the capital cost of full replacement. By replacing only the control system — leaving existing mechanical and hydraulic infrastructure intact — customers typically recover their retrofit investment in 12–18 months through energy savings, reduced scrap, and eliminated emergency maintenance calls.",
    highlights: [
      "Complete controller replacement: IMM PLC, servo drive, temperature modules, I/O",
      "Servo-hydraulic conversion: replace fixed-speed motor with AC servo motor + pump",
      "IoT connectivity: add Ethernet, remote diagnostics, and OEE dashboards",
      "Full electrical schematic redrawn in DWG + PDF and supplied with the project",
      "On-site commissioning support and operator training (typically 1–3 days)",
      "1-year warranty on all retrofitted components with 24/7 remote support",
    ],
    specs: [
      { label: "Machine Types", value: "IMM, Hydraulic Press, Wire Former, Stamping" },
      { label: "Retrofit Duration", value: "1–5 days (depending on machine size)" },
      { label: "Energy Savings", value: "30–60% (servo-hydraulic conversion)" },
      { label: "Payback Period", value: "Typically 12–18 months" },
      { label: "Warranty", value: "1 year on all replaced components" },
      { label: "Remote Support", value: "24/7 via Ethernet / VPN" },
      { label: "Documentation", value: "As-built schematic: DWG + PDF" },
      { label: "Training", value: "Operator + maintenance (4–8 hours on-site)" },
    ],
    applications: ["Legacy IMM controller replacement", "Fixed-speed to servo-hydraulic conversion", "Adding IoT / Industry 4.0 connectivity", "Safety system modernization (CE compliance)"],
    compatible: ["Any brand injection molding machine", "Hydraulic presses (all tonnages)", "Wire forming & spring coiling machines", "Stamping & transfer lines"],
  },
];

// ─── Hook ─────────────────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return w;
}

// ─── Navbar ───────────────────────────────────────────────────────────────────
function ProductsNavbar() {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const navLinks = [
    { label: "Home", href: "#/" },
    { label: "Solutions", href: "#/solutions" },
    { label: "Products", href: "#/products" },
    { label: "About", href: "#/about" },
    { label: "News", href: "#/news" },
    { label: "Contact", href: "#/contact" },
  ];

  return (
    <nav style={{
      position: "sticky", top: 0, zIndex: 1000,
      background: scrolled ? "rgba(0,31,68,0.97)" : COLORS.navyDark,
      backdropFilter: "blur(10px)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: isMobile ? "0 20px" : "0 40px", height: 64,
      boxShadow: "0 2px 16px rgba(0,0,0,0.3)",
      borderBottom: "1px solid rgba(204,0,0,0.2)",
      transition: "background 0.3s",
    }}>
      <a href="#/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
        <img src={IMAGES.logoWhite} alt="Arcuchi" style={{ height: 36, objectFit: "contain" }} />
      </a>

      {isMobile ? (
        <>
          <button onClick={() => setMenuOpen(o => !o)} style={{
            background: "none", border: "none", cursor: "pointer",
            display: "flex", flexDirection: "column", gap: 5, padding: 8,
          }}>
            {[0, 1, 2].map(i => (
              <span key={i} style={{
                display: "block", width: 24, height: 2, background: "#fff", borderRadius: 2,
                transition: "0.3s",
                transform: menuOpen && i === 0 ? "rotate(45deg) translate(5px,5px)"
                  : menuOpen && i === 1 ? "scaleX(0)"
                  : menuOpen && i === 2 ? "rotate(-45deg) translate(5px,-5px)" : "none",
              }} />
            ))}
          </button>
          {menuOpen && (
            <div style={{
              position: "absolute", top: 64, left: 0, right: 0,
              background: COLORS.navyDark, borderTop: `2px solid ${COLORS.red}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.3)", zIndex: 999,
            }}>
              {navLinks.map(({ label, href }) => (
                <a key={label} href={href} onClick={() => setMenuOpen(false)} style={{
                  display: "block", padding: "14px 24px",
                  fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 700,
                  color: label === "Products" ? "#fff" : "rgba(255,255,255,0.65)",
                  textDecoration: "none", textTransform: "uppercase", letterSpacing: "0.1em",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                  borderLeft: label === "Products" ? `3px solid ${COLORS.red}` : "3px solid transparent",
                }}>{label}</a>
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
          {navLinks.map(({ label, href }) => (
            <a key={label} href={href} style={{
              fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700,
              letterSpacing: "0.1em",
              color: label === "Products" ? "#fff" : "rgba(255,255,255,0.65)",
              textDecoration: "none", textTransform: "uppercase", paddingBottom: 2,
              borderBottom: label === "Products" ? `2px solid ${COLORS.red}` : "2px solid transparent",
            }}>{label}</a>
          ))}
          <a href="#/contact" style={{
            background: COLORS.red, color: "#fff", fontFamily: "Montserrat,sans-serif",
            fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
            padding: "9px 20px", borderRadius: 2, textDecoration: "none",
          }}>Get Quote</a>
        </div>
      )}
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function ProductsHero() {
  const w = useWindowWidth();
  const isMobile = w < 768;
  return (
    <div style={{
      background: `linear-gradient(135deg, ${COLORS.navyDark} 0%, ${COLORS.navy} 100%)`,
      padding: isMobile ? "48px 20px" : "72px 40px",
      textAlign: "center", position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "radial-gradient(circle at 15% 50%, rgba(204,0,0,0.1) 0%, transparent 45%), radial-gradient(circle at 85% 20%, rgba(0,64,128,0.3) 0%, transparent 50%)",
        pointerEvents: "none",
      }} />
      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 16 }}>
          <div style={{ width: 32, height: 2, background: COLORS.red }} />
          <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.red, letterSpacing: "0.25em", textTransform: "uppercase" }}>
            Arcuchi Product Range
          </span>
          <div style={{ width: 32, height: 2, background: COLORS.red }} />
        </div>
        <h1 style={{
          fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 28 : 44,
          fontWeight: 900, color: "#fff", textTransform: "uppercase",
          letterSpacing: "0.04em", margin: "0 0 16px",
        }}>
          Our <span style={{ color: COLORS.red }}>Products</span>
        </h1>
        <p style={{
          fontFamily: "Georgia,serif", fontSize: isMobile ? 14 : 16,
          color: "rgba(255,255,255,0.65)", lineHeight: 1.75,
          maxWidth: 580, margin: "0 auto 24px",
        }}>
          IMM controllers, AC servo systems, frequency converters and retrofit solutions — engineered in-house for precision, reliability, and lasting industrial performance.
        </p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <a href="#/" style={{ fontFamily: "Montserrat,sans-serif", fontSize: 11, color: "rgba(255,255,255,0.5)", textDecoration: "none" }}>Home</a>
          <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 10 }}>›</span>
          <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 11, color: "#fff" }}>Products</span>
        </div>
      </div>
    </div>
  );
}

// ─── Quick Navigation ─────────────────────────────────────────────────────────
function QuickNav({ active, onSelect }) {
  const w = useWindowWidth();
  const isMobile = w < 768;
  return (
    <div style={{
      background: COLORS.navyDark, position: "sticky", top: 64, zIndex: 100,
      borderBottom: `2px solid ${COLORS.red}`,
      overflowX: "auto", WebkitOverflowScrolling: "touch",
    }}>
      <div style={{
        display: "flex", minWidth: "max-content",
        padding: isMobile ? "0 20px" : "0 40px",
      }}>
        {PRODUCTS.map(p => (
          <button
            key={p.id}
            onClick={() => {
              onSelect(p.id);
              document.getElementById(p.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: isMobile ? "14px 16px" : "18px 24px",
              fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 10 : 11, fontWeight: 700,
              color: active === p.id ? "#fff" : "rgba(255,255,255,0.55)",
              textTransform: "uppercase", letterSpacing: "0.1em", whiteSpace: "nowrap",
              borderBottom: active === p.id ? `2px solid ${COLORS.red}` : "2px solid transparent",
              marginBottom: -2, transition: "color 0.2s, border-color 0.2s",
            }}
          >
            <span style={{ marginRight: 6 }}>{p.icon}</span>
            {p.title}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Product Card Grid (overview) ─────────────────────────────────────────────
function ProductOverviewGrid({ onSelect }) {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const isTablet = w < 1024;

  return (
    <div style={{ background: COLORS.greyPage, padding: isMobile ? "40px 20px" : "64px 40px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 32 : 48 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 12 }}>
            <div style={{ width: 28, height: 2, background: COLORS.red }} />
            <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.red, letterSpacing: "0.22em", textTransform: "uppercase" }}>
              What We Make
            </span>
            <div style={{ width: 28, height: 2, background: COLORS.red }} />
          </div>
          <h2 style={{
            fontFamily: "Montserrat,sans-serif", fontSize: "clamp(20px,2.4vw,30px)",
            fontWeight: 800, color: COLORS.navy, textTransform: "uppercase",
            letterSpacing: "0.03em", margin: 0,
          }}>
            Complete <span style={{ color: COLORS.red }}>Product Portfolio</span>
          </h2>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : isTablet ? "1fr 1fr" : "repeat(4, 1fr)",
          gap: isMobile ? 12 : 20,
        }}>
          {PRODUCTS.map(p => (
            <OverviewCard key={p.id} product={p} isMobile={isMobile} onSelect={onSelect} />
          ))}
        </div>
      </div>
    </div>
  );
}

function OverviewCard({ product: p, isMobile, onSelect }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={() => {
        onSelect(p.id);
        document.getElementById(p.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? COLORS.navyDark : COLORS.white,
        border: `2px solid ${hovered ? COLORS.red : "transparent"}`,
        padding: isMobile ? "20px 14px" : "28px 22px",
        cursor: "pointer", textAlign: "left",
        boxShadow: hovered ? "0 16px 40px rgba(0,0,0,0.18)" : "0 2px 12px rgba(0,0,0,0.07)",
        transform: hovered ? "translateY(-5px)" : "none",
        transition: "all 0.3s", borderRadius: 4,
      }}
    >
      <div style={{ fontSize: isMobile ? 28 : 36, marginBottom: isMobile ? 10 : 14 }}>{p.icon}</div>
      <span style={{
        display: "inline-block", marginBottom: isMobile ? 8 : 12,
        background: p.tagColor, color: "#fff",
        fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 7.5 : 8.5, fontWeight: 800,
        letterSpacing: "0.12em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 2,
      }}>{p.tag}</span>
      <h3 style={{
        fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 12 : 14, fontWeight: 800,
        color: hovered ? "#fff" : COLORS.navy, textTransform: "uppercase",
        letterSpacing: "0.04em", margin: "0 0 8px", lineHeight: 1.3,
      }}>{p.title}</h3>
      <p style={{ fontSize: isMobile ? 11 : 12, color: hovered ? "rgba(255,255,255,0.6)" : COLORS.greyDark, lineHeight: 1.65, margin: 0 }}>
        {p.subtitle.split(" — ")[0]}
      </p>
      <div style={{
        display: "flex", alignItems: "center", gap: 6, marginTop: isMobile ? 12 : 16,
        fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700,
        color: COLORS.red, textTransform: "uppercase", letterSpacing: "0.1em",
      }}>
        View Details
        <span style={{ transform: hovered ? "translateX(4px)" : "none", transition: "transform 0.25s", display: "inline-block" }}>→</span>
      </div>
    </button>
  );
}

// ─── Product Detail Section ────────────────────────────────────────────────────
function ProductDetail({ product: p, index }) {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const isTablet = w < 1024;
  const isEven = index % 2 === 0;

  return (
    <section id={p.id} style={{
      background: isEven ? COLORS.white : COLORS.greyPage,
      padding: isMobile ? "40px 20px" : "72px 40px",
      scrollMarginTop: 128,
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>

        {/* Section header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <div style={{ width: 28, height: 2, background: COLORS.red }} />
          <span style={{
            fontFamily: "Montserrat,sans-serif", fontSize: 9, fontWeight: 800,
            color: p.tagColor, letterSpacing: "0.2em", textTransform: "uppercase",
          }}>{p.tag}</span>
        </div>

        {/* Main 2-col layout */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr" : (isEven ? "1fr 1fr" : "1fr 1fr"),
          gap: isMobile ? 32 : isTablet ? 40 : 72,
          alignItems: "start",
        }}>
          {/* Content left (or right on odd) — always first on mobile */}
          <div style={{ order: isMobile ? 1 : (isEven ? 1 : 2) }}>
            <h2 style={{
              fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? "clamp(20px,5vw,26px)" : "clamp(22px,2.4vw,32px)",
              fontWeight: 900, color: COLORS.navy, textTransform: "uppercase",
              letterSpacing: "0.03em", lineHeight: 1.2, margin: "0 0 6px",
            }}>{p.title}</h2>
            <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 11, color: COLORS.greyDark, marginBottom: 18, letterSpacing: "0.04em" }}>
              {p.fullName}
            </div>
            <p style={{ fontFamily: "Georgia,serif", fontSize: isMobile ? 13.5 : 14.5, color: COLORS.textMid, lineHeight: 1.85, marginBottom: 28 }}>
              {p.desc}
            </p>

            {/* Highlights */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 800, color: COLORS.navy, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 14 }}>
                Key Features
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                {p.highlights.map((h, i) => (
                  <li key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.red, flexShrink: 0, marginTop: 7 }} />
                    <span style={{ fontFamily: "Georgia,serif", fontSize: isMobile ? 13 : 13.5, color: COLORS.textMid, lineHeight: 1.7 }}>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Applications tags */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 800, color: COLORS.navy, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 12 }}>
                Applications
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {p.applications.map(a => (
                  <span key={a} style={{
                    fontFamily: "Montserrat,sans-serif", fontSize: 9.5, fontWeight: 700,
                    color: p.tagColor, border: `1px solid ${p.tagColor}`,
                    padding: "5px 12px", borderRadius: 2, letterSpacing: "0.06em",
                    background: `${p.tagColor}14`,
                  }}>{a}</span>
                ))}
              </div>
            </div>

            {/* CTA buttons */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href="#/contact" style={{
                display: "inline-block", background: COLORS.red, color: "#fff",
                fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase",
                padding: "12px 24px", borderRadius: 2, textDecoration: "none",
              }}>Request Quote →</a>
              <a href="#/contact" style={{
                display: "inline-block", background: "transparent", color: COLORS.navy,
                fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase",
                padding: "11px 22px", borderRadius: 2, textDecoration: "none",
                border: `1px solid ${COLORS.greyMid}`,
              }}>Technical Inquiry</a>
            </div>
          </div>

          {/* Image + specs — right (or left on odd) */}
          <div style={{ order: isMobile ? 2 : (isEven ? 2 : 1) }}>
            {/* Product image */}
            <div style={{
              background: p.imgBg, overflow: "hidden", borderRadius: 4,
              marginBottom: p.extraImgs?.length ? 10 : 20, position: "relative",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            }}>
              <img src={p.img} alt={p.title} style={{
                width: "100%", height: isMobile ? 200 : 280, objectFit: "contain",
                display: "block", padding: "20px",
              }} />
              <div style={{
                position: "absolute", top: 14, right: 14,
                background: p.tagColor, color: "#fff",
                fontFamily: "Montserrat,sans-serif", fontSize: 8, fontWeight: 800,
                letterSpacing: "0.14em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 2,
              }}>{p.tag}</div>
            </div>

            {/* Extra product images gallery */}
            {p.extraImgs?.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: `repeat(${p.extraImgs.length}, 1fr)`,
                gap: 8, marginBottom: 20,
              }}>
                {p.extraImgs.map((src, i) => (
                  <div key={i} style={{
                    background: p.imgBg, borderRadius: 4, overflow: "hidden",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.09)",
                  }}>
                    <img src={src} alt={`${p.title} variant ${i + 1}`} style={{
                      width: "100%", height: isMobile ? 80 : 100,
                      objectFit: "contain", display: "block", padding: "10px",
                    }} />
                  </div>
                ))}
              </div>
            )}

            {/* Specs table */}
            <div style={{
              background: COLORS.navyDark, borderRadius: 4,
              overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}>
              <div style={{
                background: COLORS.red, padding: "12px 20px",
                fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 800,
                color: "#fff", letterSpacing: "0.16em", textTransform: "uppercase",
              }}>
                Technical Specifications
              </div>
              {p.specs.map((s, i) => (
                <div key={s.label} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                  padding: isMobile ? "10px 16px" : "12px 20px", gap: 12,
                  borderBottom: i < p.specs.length - 1 ? "1px solid rgba(255,255,255,0.07)" : "none",
                  background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)",
                }}>
                  <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 10 : 10.5, color: "rgba(255,255,255,0.55)", letterSpacing: "0.04em", flexShrink: 0 }}>
                    {s.label}
                  </span>
                  <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 10 : 11, fontWeight: 700, color: "#fff", textAlign: "right" }}>
                    {s.value}
                  </span>
                </div>
              ))}
            </div>

            {/* Compatible with */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 9.5, fontWeight: 700, color: COLORS.greyDark, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>
                Compatible With
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {p.compatible.map(c => (
                  <span key={c} style={{
                    fontFamily: "Montserrat,sans-serif", fontSize: 9.5, fontWeight: 600,
                    color: COLORS.greyDark, background: COLORS.greyCard,
                    padding: "5px 10px", borderRadius: 2,
                  }}>{c}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Why Arcuchi Products ──────────────────────────────────────────────────────
function WhyArcuchi() {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const points = [
    { icon: "🔬", title: "Self-Developed IP", desc: "100% in-house hardware design, firmware, and software — no reseller dependencies, no vendor lock-in." },
    { icon: "🏭", title: "17 Years Experience", desc: "Purpose-built for industrial control since 2007. Every feature is born from real factory floor requirements." },
    { icon: "🌐", title: "50+ Countries", desc: "14 global offices and distributors across every major manufacturing region provide local support and spare parts." },
    { icon: "🛡️", title: "1-Year Warranty", desc: "All products covered with 24/7 remote engineering support and same-day spare parts dispatch from regional warehouses." },
  ];
  return (
    <section style={{ background: COLORS.navyDark, padding: isMobile ? "40px 20px" : "64px 40px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 28 : 44 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 12 }}>
            <div style={{ width: 28, height: 2, background: COLORS.red }} />
            <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(255,150,150,0.85)", letterSpacing: "0.22em", textTransform: "uppercase" }}>
              The Arcuchi Advantage
            </span>
            <div style={{ width: 28, height: 2, background: COLORS.red }} />
          </div>
          <h2 style={{ fontFamily: "Montserrat,sans-serif", fontSize: "clamp(20px,2.4vw,30px)", fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: "0.03em", margin: 0 }}>
            Why Choose <span style={{ color: COLORS.red }}>Our Products?</span>
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: isMobile ? 12 : 2 }}>
          {points.map(p => (
            <WhyPoint key={p.title} {...p} isMobile={isMobile} />
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyPoint({ icon, title, desc, isMobile }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: isMobile ? "20px 14px" : "32px 24px",
        background: hovered ? "rgba(204,0,0,0.12)" : "rgba(255,255,255,0.04)",
        borderLeft: hovered ? `3px solid ${COLORS.red}` : "3px solid transparent",
        transition: "all 0.3s", cursor: "default",
        borderRadius: isMobile ? 4 : 0,
      }}
    >
      <div style={{ fontSize: isMobile ? 24 : 32, marginBottom: isMobile ? 10 : 14 }}>{icon}</div>
      <h3 style={{ fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 11 : 13, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: isMobile ? 6 : 10 }}>{title}</h3>
      <p style={{ fontSize: isMobile ? 11 : 12, color: "rgba(255,255,255,0.52)", lineHeight: 1.75 }}>{desc}</p>
    </div>
  );
}

// ─── CTA Banner ───────────────────────────────────────────────────────────────
function ProductsCTA() {
  const w = useWindowWidth();
  const isMobile = w < 768;
  return (
    <section style={{
      position: "relative", overflow: "hidden",
      padding: isMobile ? "48px 20px" : "72px 40px",
      background: COLORS.navy,
    }}>
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "radial-gradient(circle at 80% 50%, rgba(204,0,0,0.1) 0%, transparent 55%)",
        pointerEvents: "none",
      }} />
      <div style={{ position: "relative", maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ width: 28, height: 2, background: COLORS.red }} />
          <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(255,150,150,0.85)", letterSpacing: "0.22em", textTransform: "uppercase" }}>Ready to Order?</span>
          <div style={{ width: 28, height: 2, background: COLORS.red }} />
        </div>
        <h2 style={{
          fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? "clamp(22px,6vw,32px)" : "clamp(26px,3vw,38px)",
          fontWeight: 900, color: "#fff", textTransform: "uppercase",
          letterSpacing: "0.03em", lineHeight: 1.2, margin: "0 0 16px",
        }}>
          Talk to an Engineer About <span style={{ color: COLORS.red }}>Your Application</span>
        </h2>
        <p style={{ fontFamily: "Georgia,serif", fontSize: isMobile ? 13 : 15, color: "rgba(255,255,255,0.6)", lineHeight: 1.8, marginBottom: 32, maxWidth: 560, margin: "0 auto 32px" }}>
          Our pre-sales engineering team provides free application reviews, product selection guidance, and detailed cost-benefit comparisons — no commitment required.
        </p>
        <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
          <a href="#/contact" style={{
            display: "block", background: COLORS.red, color: "#fff",
            fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 700,
            letterSpacing: "0.1em", textTransform: "uppercase",
            padding: "14px 32px", textDecoration: "none", flex: isMobile ? 1 : "none",
            textAlign: "center",
          }}>Get a Free Quote →</a>
          <a href="https://wa.me/8613825271417" target="_blank" rel="noreferrer" style={{
            display: "block", background: "transparent", color: "#fff",
            fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 600,
            letterSpacing: "0.08em", textTransform: "uppercase",
            padding: "13px 28px", border: "1px solid rgba(255,255,255,0.35)",
            textDecoration: "none", flex: isMobile ? 1 : "none", textAlign: "center",
          }}>💬 WhatsApp Us</a>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function ProductsFooter() {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const cols = [
    { title: "Products", links: [["IMM Controller", "#imm-controller"], ["Servo System", "#servo-system"], ["Frequency Converter", "#frequency-converter"], ["Machine Retrofit", "#retrofit"]] },
    { title: "Company", links: [["About Us", "#/about"], ["News & Events", "#/news"], ["Careers", "#/"]] },
    { title: "Support", links: [["Contact Us", "#/contact"], ["Technical Docs", "#/contact"], ["AMC Program", "#/contact"], ["WhatsApp", "https://wa.me/8613825271417"]] },
  ];
  return (
    <footer style={{ background: COLORS.navyDark, padding: isMobile ? "40px 20px 20px" : "48px 40px 24px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "2fr 1fr 1fr 1fr", gap: isMobile ? 28 : 40, marginBottom: 40 }}>
          <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
            <img src={IMAGES.logoFooter} alt="Arcuchi" style={{ height: 38, objectFit: "contain", marginBottom: 14, display: "block" }} />
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.42)", lineHeight: 1.8, maxWidth: 260, margin: 0 }}>
              Precision-engineered industrial control products for manufacturers across 50+ countries.
            </p>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.8)", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, paddingBottom: 8, borderBottom: `1px solid ${COLORS.red}` }}>
                {col.title}
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {col.links.map(([label, href]) => (
                  <li key={label}>
                    <a href={href} style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", textDecoration: "none" }}>{label}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 20, textAlign: "center" }}>
          <p style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, color: "rgba(255,255,255,0.28)", margin: 0 }}>
            © 2025 Shenzhen Arcuchi Technology Co., Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

// ─── Page Root ────────────────────────────────────────────────────────────────
export default function ProductsPage() {
  const [activeProduct, setActiveProduct] = useState(PRODUCTS[0].id);

  useEffect(() => {
    document.title = "Products — Arcuchi CNC";
    window.scrollTo(0, 0);

    // Update active tab on scroll
    const onScroll = () => {
      for (const p of PRODUCTS) {
        const el = document.getElementById(p.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom >= 200) {
            setActiveProduct(p.id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div style={{ fontFamily: "Montserrat,sans-serif", background: COLORS.white, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Georgia&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
      `}</style>
      <ProductsNavbar />
      <ProductsHero />
      <QuickNav active={activeProduct} onSelect={setActiveProduct} />
      <ProductOverviewGrid onSelect={setActiveProduct} />
      {PRODUCTS.map((p, i) => (
        <ProductDetail key={p.id} product={p} index={i} />
      ))}
      <WhyArcuchi />
      <ProductsCTA />
      <ProductsFooter />
    </div>
  );
}
